import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  casesAPI, tasksAPI, timeEntriesAPI, documentsAPI,
  notesAPI, expensesAPI, invoicesAPI, eventsAPI, employeesAPI,
} from '../services/api';
import StatusBadge from '../components/StatusBadge';
import Modal from '../components/Modal';
import {
  ArrowLeft, Download, Plus, Clock, CheckSquare, FileText,
  DollarSign, StickyNote, Briefcase, Receipt, Calendar,
  Edit2, Wand2, Upload, ChevronDown,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';

// ── Tab definitions ────────────────────────────────────────────────────────────
const TABS = [
  { id: 'overview',   label: 'Overview',    icon: Briefcase },
  { id: 'tasks',      label: 'Tasks',       icon: CheckSquare },
  { id: 'time',       label: 'Time',        icon: Clock },
  { id: 'documents',  label: 'Documents',   icon: FileText },
  { id: 'notes',      label: 'Notes',       icon: StickyNote },
  { id: 'expenses',   label: 'Expenses',    icon: DollarSign },
  { id: 'invoices',   label: 'Invoices',    icon: Receipt },
  { id: 'calendar',   label: 'Calendar',    icon: Calendar },
];

const TEMPLATES = [
  'Summons','Heads of Arguments','Bail Application',
  'Affidavit','Notice of Motion','Founding Affidavit','Retainer Agreement',
];

const today = () => new Date().toISOString().slice(0, 10);

// ── Reusable empty-state ───────────────────────────────────────────────────────
function Empty({ icon: Icon, label }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-gray-300">
      <Icon size={44} className="mb-3" />
      <p className="text-sm text-gray-400">{label}</p>
    </div>
  );
}

// ── Section header with Add button ────────────────────────────────────────────
function SectionHeader({ title, count, onAdd, addLabel }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-base font-semibold text-gray-800">
        {title}
        {count > 0 && (
          <span className="ml-2 text-xs font-normal text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
            {count}
          </span>
        )}
      </h3>
      {onAdd && (
        <button className="btn-primary text-xs py-1.5 px-3 flex items-center gap-1.5" onClick={onAdd}>
          <Plus size={13} /> {addLabel}
        </button>
      )}
    </div>
  );
}

export default function CaseDetail() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [summary,     setSummary]     = useState(null);
  const [tasks,       setTasks]       = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [documents,   setDocuments]   = useState([]);
  const [expenses,    setExpenses]    = useState([]);
  const [notes,       setNotes]       = useState([]);
  const [invoices,    setInvoices]    = useState([]);
  const [events,      setEvents]      = useState([]);
  const [employees,   setEmployees]   = useState([]);
  const [tab,         setTab]         = useState('overview');
  const [saving,      setSaving]      = useState(false);
  const [generating,  setGenerating]  = useState(false);

  // Modal state
  const [taskModal,    setTaskModal]    = useState(false);
  const [timeModal,    setTimeModal]    = useState(false);
  const [noteModal,    setNoteModal]    = useState(false);
  const [expenseModal, setExpenseModal] = useState(false);
  const [docModal,     setDocModal]     = useState(false);
  const [genModal,     setGenModal]     = useState(false);
  const [uploadFile,   setUploadFile]   = useState(null);

  // Form state
  const [taskForm,    setTaskForm]    = useState({ title: '', description: '', priority: 'Medium', due_date: '', time_estimate_hours: '', assigned_to: '', billable: true });
  const [timeForm,    setTimeForm]    = useState({ date: today(), duration_hours: '', description: '', billing_status: 'Unbilled' });
  const [noteForm,    setNoteForm]    = useState({ subject: '', content: '', is_private: false });
  const [expForm,     setExpForm]     = useState({ date_incurred: today(), amount: '', category: 'Other', description: '' });
  const [docForm,     setDocForm]     = useState({ document_name: '', document_type: 'Other', version: '1.0', description: '' });
  const [docType,     setDocType]     = useState('Summons');

  // ── Loaders ──────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    const [sum, t, te, d, e, n, inv, ev] = await Promise.all([
      casesAPI.summary(id),
      tasksAPI.list({ case: id }),
      timeEntriesAPI.list({ case: id }),
      documentsAPI.list({ case: id }),
      expensesAPI.list({ case: id }),
      notesAPI.list({ case: id }),
      invoicesAPI.list({ case: id }),
      eventsAPI.list({ case: id }),
    ]);
    setSummary(sum.data);
    setTasks(t.data.results || t.data);
    setTimeEntries(te.data.results || te.data);
    setDocuments(d.data.results || d.data);
    setExpenses(e.data.results || e.data);
    setNotes(n.data.results || n.data);
    setInvoices(inv.data.results || inv.data);
    setEvents(ev.data.results || ev.data);
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    employeesAPI.list({ status: 'Active' }).then(r => setEmployees(r.data.results || r.data)).catch(() => {});
  }, []);

  if (!summary) return (
    <div className="flex items-center justify-center h-96">
      <div className="animate-spin w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full" />
    </div>
  );

  const c = summary.case;

  // ── Create handlers ───────────────────────────────────────────────────────────
  const createTask = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await tasksAPI.create({
        ...taskForm,
        case: id,
        assigned_to: taskForm.assigned_to || null,
        time_estimate_hours: taskForm.time_estimate_hours || 0,
      });
      toast.success('Task added');
      setTaskModal(false);
      setTaskForm({ title: '', description: '', priority: 'Medium', due_date: '', time_estimate_hours: '', assigned_to: '', billable: true });
      load();
    } catch { toast.error('Failed to add task'); }
    finally { setSaving(false); }
  };

  const createTime = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await timeEntriesAPI.create({ ...timeForm, case: id, employee: user.id });
      toast.success('Time entry logged');
      setTimeModal(false);
      setTimeForm({ date: today(), duration_hours: '', description: '', billing_status: 'Unbilled' });
      load();
    } catch { toast.error('Failed to log time'); }
    finally { setSaving(false); }
  };

  const createNote = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await notesAPI.create({ ...noteForm, case: id });
      toast.success('Note added');
      setNoteModal(false);
      setNoteForm({ subject: '', content: '', is_private: false });
      load();
    } catch { toast.error('Failed to add note'); }
    finally { setSaving(false); }
  };

  const createExpense = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await expensesAPI.create({ ...expForm, case: id });
      toast.success('Expense recorded');
      setExpenseModal(false);
      setExpForm({ date_incurred: today(), amount: '', category: 'Other', description: '' });
      load();
    } catch { toast.error('Failed to record expense'); }
    finally { setSaving(false); }
  };

  const uploadDoc = async (e) => {
    e.preventDefault();
    if (!uploadFile) return toast.error('Select a file first');
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(docForm).forEach(([k, v]) => v && fd.append(k, v));
      fd.append('case', id);
      fd.append('file', uploadFile);
      await documentsAPI.upload(fd);
      toast.success('Document uploaded');
      setDocModal(false);
      setDocForm({ document_name: '', document_type: 'Other', version: '1.0', description: '' });
      setUploadFile(null);
      load();
    } catch { toast.error('Upload failed'); }
    finally { setSaving(false); }
  };

  const generateDoc = async () => {
    setGenerating(true);
    try {
      const res = await documentsAPI.generate({ template_type: docType, case_id: id, format: 'docx' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${docType.replace(/ /g, '_')}_${c.case_number || id}.docx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`${docType} generated`);
      setGenModal(false);
    } catch { toast.error('Failed to generate document'); }
    finally { setGenerating(false); }
  };

  // ── Colour helpers ────────────────────────────────────────────────────────────
  const taskColour = { High: 'text-red-600', Medium: 'text-amber-600', Low: 'text-green-600' };

  return (
    <div className="flex flex-col h-full bg-gray-50">

      {/* ── Case header ─────────────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-gray-200 px-6 pt-5 pb-0">
        {/* Breadcrumb */}
        <button
          className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-blue-600 mb-3 transition-colors"
          onClick={() => navigate('/cases')}
        >
          <ArrowLeft size={13} /> Cases
        </button>

        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-4">
          {/* Title + meta */}
          <div>
            <div className="flex items-center gap-3 mb-1 flex-wrap">
              <h1 className="text-xl font-bold text-gray-900">{c.case_title}</h1>
              <StatusBadge status={c.status} />
              {c.conflict_check_status === 'Cleared' && (
                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">Conflict Cleared</span>
              )}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500">
              {c.case_number && <span>#{c.case_number}</span>}
              <span>{c.case_type}</span>
              <span className="text-blue-600 font-medium cursor-pointer hover:underline"
                onClick={() => navigate(`/clients/${c.client}`)}>
                {c.client_name}
              </span>
              {c.attorney_name && <span>Atty: {c.attorney_name}</span>}
              {c.court_name && <span>Court: {c.court_name}</span>}
              {c.opposing_party && <span>Vs: {c.opposing_party}</span>}
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex gap-2 flex-shrink-0">
            <button className="btn-secondary text-sm flex items-center gap-1.5"
              onClick={() => navigate(`/cases`)}>
              <Edit2 size={14} /> Edit
            </button>
          </div>
        </div>

        {/* KPI strip */}
        <div className="flex gap-6 mb-4 flex-wrap">
          {[
            { label: 'Hours Logged',  value: `${summary.total_hours.toFixed(1)}h`,           color: 'text-blue-600' },
            { label: 'Total Billed',  value: `US$ ${summary.total_billed.toLocaleString()}`,  color: 'text-green-600' },
            { label: 'Expenses',      value: `US$ ${summary.total_expenses.toLocaleString()}`, color: 'text-amber-600' },
            { label: 'Tasks',         value: summary.task_count,                              color: 'text-purple-600' },
            { label: 'Documents',     value: summary.document_count,                          color: 'text-gray-600' },
          ].map(({ label, value, color }) => (
            <div key={label} className="text-center min-w-[72px]">
              <div className={`text-lg font-bold ${color}`}>{value}</div>
              <div className="text-xs text-gray-400">{label}</div>
            </div>
          ))}
        </div>

        {/* ── Horizontal tabs ───────────────────────────────────────────────────── */}
        <nav className="flex gap-0 overflow-x-auto">
          {TABS.map(({ id: tid, label, icon: Icon }) => (
            <button
              key={tid}
              onClick={() => setTab(tid)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors
                ${tab === tid
                  ? 'border-blue-600 text-blue-700 bg-blue-50/50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
            >
              <Icon size={14} />
              {label}
              {/* badge counts */}
              {tid === 'tasks'     && tasks.length     > 0 && <span className="ml-1 text-xs bg-gray-200 text-gray-600 px-1.5 rounded-full">{tasks.length}</span>}
              {tid === 'time'      && timeEntries.length > 0 && <span className="ml-1 text-xs bg-gray-200 text-gray-600 px-1.5 rounded-full">{timeEntries.length}</span>}
              {tid === 'documents' && documents.length  > 0 && <span className="ml-1 text-xs bg-gray-200 text-gray-600 px-1.5 rounded-full">{documents.length}</span>}
              {tid === 'notes'     && notes.length      > 0 && <span className="ml-1 text-xs bg-gray-200 text-gray-600 px-1.5 rounded-full">{notes.length}</span>}
              {tid === 'expenses'  && expenses.length   > 0 && <span className="ml-1 text-xs bg-gray-200 text-gray-600 px-1.5 rounded-full">{expenses.length}</span>}
              {tid === 'invoices'  && invoices.length   > 0 && <span className="ml-1 text-xs bg-gray-200 text-gray-600 px-1.5 rounded-full">{invoices.length}</span>}
            </button>
          ))}
        </nav>
      </div>

      {/* ── Tab content ─────────────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto p-6">

        {/* OVERVIEW ─────────────────────────────────────────────────────────── */}
        {tab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* Case details */}
            <div className="lg:col-span-2 card">
              <h3 className="font-semibold text-gray-900 mb-4">Case Details</h3>
              <dl className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
                {[
                  ['Case Number',   c.case_number || '—'],
                  ['Type',          c.case_type],
                  ['Status',        <StatusBadge status={c.status} />],
                  ['Opened',        c.opened_date],
                  ['Closed',        c.closed_date || '—'],
                  ['Conflict Check', <StatusBadge status={c.conflict_check_status} />],
                  ['Court Date',    c.court_date || '—'],
                  ['Opposing Party',c.opposing_party || '—'],
                ].map(([label, value]) => (
                  <div key={label} className="flex flex-col gap-0.5">
                    <dt className="text-xs text-gray-400 font-medium uppercase tracking-wide">{label}</dt>
                    <dd className="text-gray-800 font-medium">{value}</dd>
                  </div>
                ))}
              </dl>
              {c.case_description && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-1.5">Description</p>
                  <p className="text-sm text-gray-700 leading-relaxed">{c.case_description}</p>
                </div>
              )}
            </div>

            {/* Quick actions */}
            <div className="space-y-4">
              <div className="card">
                <h3 className="font-semibold text-gray-900 mb-3">Quick Actions</h3>
                <div className="space-y-2">
                  {[
                    { label: 'Add Task',      icon: CheckSquare, action: () => setTaskModal(true),    color: 'text-purple-600 bg-purple-50 hover:bg-purple-100' },
                    { label: 'Log Time',      icon: Clock,       action: () => setTimeModal(true),    color: 'text-blue-600 bg-blue-50 hover:bg-blue-100' },
                    { label: 'Add Note',      icon: StickyNote,  action: () => setNoteModal(true),    color: 'text-amber-600 bg-amber-50 hover:bg-amber-100' },
                    { label: 'Upload Document', icon: Upload,    action: () => setDocModal(true),     color: 'text-green-600 bg-green-50 hover:bg-green-100' },
                    { label: 'Generate Template', icon: Wand2,  action: () => setGenModal(true),     color: 'text-indigo-600 bg-indigo-50 hover:bg-indigo-100' },
                    { label: 'Record Expense',icon: DollarSign,  action: () => setExpenseModal(true), color: 'text-red-600 bg-red-50 hover:bg-red-100' },
                  ].map(({ label, icon: Icon, action, color }) => (
                    <button key={label} onClick={action}
                      className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${color}`}>
                      <Icon size={15} /> {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TASKS ────────────────────────────────────────────────────────────── */}
        {tab === 'tasks' && (
          <>
            <SectionHeader title="Tasks" count={tasks.length} onAdd={() => setTaskModal(true)} addLabel="Add Task" />
            <div className="card p-0 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>{['Task', 'Assigned To', 'Priority', 'Est. Hours', 'Due Date', 'Status'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">{h}</th>
                  ))}</tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {tasks.length === 0
                    ? <tr><td colSpan={6}><Empty icon={CheckSquare} label="No tasks yet — add the first one" /></td></tr>
                    : tasks.map(t => (
                      <tr key={t.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900">{t.title}</td>
                        <td className="px-4 py-3 text-gray-500">{t.assigned_to_name || '—'}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-semibold ${taskColour[t.priority] || 'text-gray-500'}`}>{t.priority}</span>
                        </td>
                        <td className="px-4 py-3 text-gray-500">{t.time_estimate_hours ? `${t.time_estimate_hours}h` : '—'}</td>
                        <td className="px-4 py-3 text-gray-500">{t.due_date || '—'}</td>
                        <td className="px-4 py-3"><StatusBadge status={t.status} /></td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* TIME ─────────────────────────────────────────────────────────────── */}
        {tab === 'time' && (
          <>
            <SectionHeader title="Time Entries" count={timeEntries.length} onAdd={() => setTimeModal(true)} addLabel="Log Time" />
            <div className="card p-0 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>{['Date', 'Staff', 'Description', 'Hours', 'Rate (US$/h)', 'Amount', 'Status'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">{h}</th>
                  ))}</tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {timeEntries.length === 0
                    ? <tr><td colSpan={7}><Empty icon={Clock} label="No time logged yet" /></td></tr>
                    : timeEntries.map(t => (
                      <tr key={t.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-gray-500">{t.date}</td>
                        <td className="px-4 py-3 text-gray-700">{t.employee_name}</td>
                        <td className="px-4 py-3 text-gray-600 max-w-xs truncate">{t.description}</td>
                        <td className="px-4 py-3 font-semibold text-blue-700">{t.duration_hours}h</td>
                        <td className="px-4 py-3 text-gray-500">{t.hourly_rate}</td>
                        <td className="px-4 py-3 font-semibold text-green-700">US$ {parseFloat(t.amount || 0).toFixed(2)}</td>
                        <td className="px-4 py-3"><StatusBadge status={t.billing_status} /></td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* DOCUMENTS ───────────────────────────────────────────────────────── */}
        {tab === 'documents' && (
          <>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-gray-800">
                Documents
                {documents.length > 0 && <span className="ml-2 text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{documents.length}</span>}
              </h3>
              <div className="flex gap-2">
                <button className="btn-secondary text-xs py-1.5 px-3 flex items-center gap-1.5" onClick={() => setGenModal(true)}>
                  <Wand2 size={13} /> Generate Template
                </button>
                <button className="btn-primary text-xs py-1.5 px-3 flex items-center gap-1.5" onClick={() => setDocModal(true)}>
                  <Upload size={13} /> Upload
                </button>
              </div>
            </div>
            {documents.length === 0
              ? <div className="card"><Empty icon={FileText} label="No documents yet — upload or generate one" /></div>
              : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {documents.map(d => (
                    <div key={d.id} className="card p-4 hover:shadow-md transition-shadow group">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                          <FileText size={18} className="text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 text-sm truncate">{d.document_name}</p>
                          <p className="text-xs text-gray-400">{d.document_type} · v{d.version}</p>
                          <p className="text-xs text-gray-400">{d.file_size_kb} KB · {new Date(d.created_date).toLocaleDateString()}</p>
                        </div>
                      </div>
                      <a href={`http://localhost:8000${d.file}`} target="_blank" rel="noopener noreferrer"
                        className="flex items-center justify-center gap-1.5 text-xs text-blue-600 border border-blue-200 rounded-lg py-1.5 hover:bg-blue-50 transition-colors">
                        <Download size={11} /> Download
                      </a>
                    </div>
                  ))}
                </div>
              )}
          </>
        )}

        {/* NOTES ───────────────────────────────────────────────────────────── */}
        {tab === 'notes' && (
          <>
            <SectionHeader title="Notes" count={notes.length} onAdd={() => setNoteModal(true)} addLabel="Add Note" />
            {notes.length === 0
              ? <div className="card"><Empty icon={StickyNote} label="No notes yet" /></div>
              : (
                <div className="space-y-3">
                  {notes.map(n => (
                    <div key={n.id} className="card p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 bg-blue-700 rounded-full flex items-center justify-center">
                            <span className="text-white text-xs font-bold">{n.employee_name?.[0] || '?'}</span>
                          </div>
                          <span className="text-sm font-semibold text-gray-800">{n.employee_name}</span>
                          {n.subject && <span className="text-sm text-gray-500">— {n.subject}</span>}
                          {n.is_private && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Private</span>}
                        </div>
                        <span className="text-xs text-gray-400">{new Date(n.created_date).toLocaleString('en-GB', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' })}</span>
                      </div>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed pl-9">{n.content}</p>
                    </div>
                  ))}
                </div>
              )}
          </>
        )}

        {/* EXPENSES ────────────────────────────────────────────────────────── */}
        {tab === 'expenses' && (
          <>
            <SectionHeader title="Expenses" count={expenses.length} onAdd={() => setExpenseModal(true)} addLabel="Record Expense" />
            <div className="card p-0 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>{['Date', 'Category', 'Description', 'Amount', 'Billed to Client', 'Approved'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">{h}</th>
                  ))}</tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {expenses.length === 0
                    ? <tr><td colSpan={6}><Empty icon={DollarSign} label="No expenses recorded" /></td></tr>
                    : expenses.map(e => (
                      <tr key={e.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-gray-500">{e.date_incurred}</td>
                        <td className="px-4 py-3"><span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full">{e.category}</span></td>
                        <td className="px-4 py-3 text-gray-700">{e.description}</td>
                        <td className="px-4 py-3 font-semibold text-gray-900">US$ {parseFloat(e.amount).toFixed(2)}</td>
                        <td className="px-4 py-3"><StatusBadge status={e.billed_to_client ? 'Yes' : 'No'} /></td>
                        <td className="px-4 py-3"><StatusBadge status={e.approved ? 'Approved' : 'Pending'} /></td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* INVOICES ────────────────────────────────────────────────────────── */}
        {tab === 'invoices' && (
          <>
            <SectionHeader title="Invoices" count={invoices.length} onAdd={() => navigate('/invoices')} addLabel="New Invoice" />
            <div className="card p-0 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>{['Invoice #', 'Issue Date', 'Due Date', 'Amount', 'Paid', 'Balance', 'Status'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">{h}</th>
                  ))}</tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {invoices.length === 0
                    ? <tr><td colSpan={7}><Empty icon={Receipt} label="No invoices for this case" /></td></tr>
                    : invoices.map(inv => (
                      <tr key={inv.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-semibold text-blue-700">INV-{inv.invoice_number}</td>
                        <td className="px-4 py-3 text-gray-500">{inv.issue_date}</td>
                        <td className="px-4 py-3 text-gray-500">{inv.due_date}</td>
                        <td className="px-4 py-3 font-semibold">US$ {parseFloat(inv.total_amount).toFixed(2)}</td>
                        <td className="px-4 py-3 text-green-700">US$ {parseFloat(inv.paid_amount || 0).toFixed(2)}</td>
                        <td className="px-4 py-3 text-amber-700">US$ {parseFloat(inv.balance_due || 0).toFixed(2)}</td>
                        <td className="px-4 py-3"><StatusBadge status={inv.status} /></td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* CALENDAR ────────────────────────────────────────────────────────── */}
        {tab === 'calendar' && (
          <>
            <SectionHeader title="Events & Hearings" count={events.length} onAdd={() => navigate('/calendar')} addLabel="Add Event" />
            {events.length === 0
              ? <div className="card"><Empty icon={Calendar} label="No events scheduled for this case" /></div>
              : (
                <div className="space-y-3">
                  {events.map(ev => (
                    <div key={ev.id} className="card p-4 flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl flex flex-col items-center justify-center flex-shrink-0 text-white"
                        style={{ backgroundColor: ev.color || '#3B82F6' }}>
                        <span className="text-lg font-bold leading-none">{new Date(ev.start_datetime).getDate()}</span>
                        <span className="text-xs opacity-80">{new Date(ev.start_datetime).toLocaleString('en', { month: 'short' })}</span>
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">{ev.title}</p>
                        <p className="text-sm text-gray-500">{ev.event_type} · {new Date(ev.start_datetime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                        {ev.location && <p className="text-xs text-gray-400 mt-0.5">{ev.location}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
          </>
        )}
      </div>

      {/* ── Modals ──────────────────────────────────────────────────────────────── */}

      {/* Add Task */}
      <Modal isOpen={taskModal} onClose={() => setTaskModal(false)} title="Add Task">
        <form onSubmit={createTask} className="space-y-4">
          <div>
            <label className="label">Title *</label>
            <input className="input" required value={taskForm.title} onChange={e => setTaskForm(f => ({...f, title: e.target.value}))} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Assigned To</label>
              <select className="input" value={taskForm.assigned_to} onChange={e => setTaskForm(f => ({...f, assigned_to: e.target.value}))}>
                <option value="">— Unassigned —</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name} ({emp.role})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Priority</label>
              <select className="input" value={taskForm.priority} onChange={e => setTaskForm(f => ({...f, priority: e.target.value}))}>
                {['High', 'Medium', 'Low'].map(p => <option key={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Due Date</label>
              <input type="date" className="input" value={taskForm.due_date} onChange={e => setTaskForm(f => ({...f, due_date: e.target.value}))} />
            </div>
            <div>
              <label className="label">Estimated Hours</label>
              <input type="number" step="0.5" min="0" className="input" value={taskForm.time_estimate_hours} onChange={e => setTaskForm(f => ({...f, time_estimate_hours: e.target.value}))} />
            </div>
          </div>
          <div>
            <label className="label">Description</label>
            <textarea className="input" rows={2} value={taskForm.description} onChange={e => setTaskForm(f => ({...f, description: e.target.value}))} />
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="task-billable"
              checked={taskForm.billable}
              onChange={e => setTaskForm(f => ({...f, billable: e.target.checked}))}
              className="rounded"
            />
            <label htmlFor="task-billable" className="text-sm text-gray-700">Billable task</label>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" className="btn-secondary" onClick={() => setTaskModal(false)}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Adding…' : 'Add Task'}</button>
          </div>
        </form>
      </Modal>

      {/* Log Time */}
      <Modal isOpen={timeModal} onClose={() => setTimeModal(false)} title="Log Time">
        <form onSubmit={createTime} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><label className="label">Date *</label><input type="date" className="input" required value={timeForm.date} onChange={e => setTimeForm(f => ({...f, date: e.target.value}))} /></div>
            <div><label className="label">Duration (hours) *</label><input type="number" step="0.25" min="0.25" className="input" required value={timeForm.duration_hours} onChange={e => setTimeForm(f => ({...f, duration_hours: e.target.value}))} /></div>
          </div>
          <div><label className="label">Status</label>
            <select className="input" value={timeForm.billing_status} onChange={e => setTimeForm(f => ({...f, billing_status: e.target.value}))}>
              {['Unbilled','Billed','Written Off'].map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div><label className="label">Description *</label><textarea className="input" rows={3} required value={timeForm.description} onChange={e => setTimeForm(f => ({...f, description: e.target.value}))} /></div>
          <div className="flex justify-end gap-3 pt-2"><button type="button" className="btn-secondary" onClick={() => setTimeModal(false)}>Cancel</button><button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Log Time'}</button></div>
        </form>
      </Modal>

      {/* Add Note */}
      <Modal isOpen={noteModal} onClose={() => setNoteModal(false)} title="Add Note">
        <form onSubmit={createNote} className="space-y-4">
          <div><label className="label">Subject</label><input className="input" value={noteForm.subject} onChange={e => setNoteForm(f => ({...f, subject: e.target.value}))} /></div>
          <div><label className="label">Note *</label><textarea className="input" rows={5} required value={noteForm.content} onChange={e => setNoteForm(f => ({...f, content: e.target.value}))} /></div>
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input type="checkbox" checked={noteForm.is_private} onChange={e => setNoteForm(f => ({...f, is_private: e.target.checked}))} />
            Private (only visible to me)
          </label>
          <div className="flex justify-end gap-3 pt-2"><button type="button" className="btn-secondary" onClick={() => setNoteModal(false)}>Cancel</button><button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save Note'}</button></div>
        </form>
      </Modal>

      {/* Record Expense */}
      <Modal isOpen={expenseModal} onClose={() => setExpenseModal(false)} title="Record Expense">
        <form onSubmit={createExpense} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><label className="label">Date *</label><input type="date" className="input" required value={expForm.date_incurred} onChange={e => setExpForm(f => ({...f, date_incurred: e.target.value}))} /></div>
            <div><label className="label">Amount (US$) *</label><input type="number" step="0.01" min="0.01" className="input" required value={expForm.amount} onChange={e => setExpForm(f => ({...f, amount: e.target.value}))} /></div>
            <div className="col-span-2"><label className="label">Category</label>
              <select className="input" value={expForm.category} onChange={e => setExpForm(f => ({...f, category: e.target.value}))}>
                {['Filing Fees','Travel','Research','Postage','Printing','Telephone','Expert Witness','Court Reporter','Sheriff Fees','Stamp Duty','Other'].map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div><label className="label">Description *</label><textarea className="input" rows={2} required value={expForm.description} onChange={e => setExpForm(f => ({...f, description: e.target.value}))} /></div>
          <div className="flex justify-end gap-3 pt-2"><button type="button" className="btn-secondary" onClick={() => setExpenseModal(false)}>Cancel</button><button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Record Expense'}</button></div>
        </form>
      </Modal>

      {/* Upload Document */}
      <Modal isOpen={docModal} onClose={() => setDocModal(false)} title="Upload Document">
        <form onSubmit={uploadDoc} className="space-y-4">
          <div><label className="label">Document Name *</label><input className="input" required value={docForm.document_name} onChange={e => setDocForm(f => ({...f, document_name: e.target.value}))} /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="label">Type</label>
              <select className="input" value={docForm.document_type} onChange={e => setDocForm(f => ({...f, document_type: e.target.value}))}>
                {['Pleading','Contract','Evidence','Court Order','Summons','Affidavit','Notice of Motion','Title Deed','Receipt','Invoice','Retainer Agreement','Other'].map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div><label className="label">Version</label><input className="input" value={docForm.version} onChange={e => setDocForm(f => ({...f, version: e.target.value}))} /></div>
          </div>
          <div><label className="label">File *</label><input type="file" className="input" required onChange={e => setUploadFile(e.target.files[0])} /></div>
          <div><label className="label">Description</label><textarea className="input" rows={2} value={docForm.description} onChange={e => setDocForm(f => ({...f, description: e.target.value}))} /></div>
          <div className="flex justify-end gap-3 pt-2"><button type="button" className="btn-secondary" onClick={() => setDocModal(false)}>Cancel</button><button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Uploading…' : 'Upload'}</button></div>
        </form>
      </Modal>

      {/* Generate Template */}
      <Modal isOpen={genModal} onClose={() => setGenModal(false)} title="Generate Legal Document">
        <div className="space-y-4">
          <div><label className="label">Template</label>
            <select className="input" value={docType} onChange={e => setDocType(e.target.value)}>
              {TEMPLATES.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div className="bg-blue-50 rounded-lg p-3 text-xs text-blue-700">
            Auto-populated with: client name, case number, attorney, court name, opposing party, and firm details.
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button className="btn-secondary" onClick={() => setGenModal(false)}>Cancel</button>
            <button className="btn-primary flex items-center gap-2" onClick={generateDoc} disabled={generating}>
              <Wand2 size={14} />{generating ? 'Generating…' : `Generate ${docType}`}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
